import {
  Activity,
  Building2,
  Contact,
  Gavel,
  FileSignature,
  FileText,
  Tags,
  Target,
  TrendingUp,
  UserPlus,
  Workflow,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Roles with CRM access (mirrors the CRM nav gate). */
const CRM_ROLES: Role[] = ['super_admin', 'founder', 'sales_manager', 'lead_gen'];
/** Proposals & Contracts carry money — Lead Gen excluded, Accountant added (C7). */
const CRM_MONEY_ROLES: Role[] = ['super_admin', 'founder', 'sales_manager', 'accountant'];

export const CRM_GUIDES: Guide[] = [
  {
    slug: 'crm-overview',
    title: 'How the pipeline works',
    module: 'crm',
    summary: 'The Lead → Deal → Proposal → Contract → Project flow at a glance.',
    icon: Workflow,
    roles: CRM_ROLES,
    body: `The CRM follows one path — from a first touch to signed, delivered work.
Each step builds on the last, so nothing is re-keyed and every record stays linked.

\`\`\`
 LEAD        DEAL          PROPOSAL       CONTRACT      PROJECT
 ───────▶    ───────▶      ───────▶       ───────▶      ───────
 a sniff     a real        a priced       a signed      delivery
 of interest opportunity   offer          agreement     begins
 (qualify)   (pipeline)    (line items)   (e-sign)      (auto-created)
\`\`\`

### The stages
1. **Lead** — raw interest from any channel. You **qualify** it, then **convert** it to a deal.
2. **Deal** — a tracked opportunity moving through pipeline stages, with an owner and value.
3. **Proposal** — the priced offer (snapshotted line items) you send for sign-off.
4. **Contract** — the agreement; **signing it provisions a Project** automatically.
5. **Project** — delivery starts (handled in the Projects module).

### Two shortcuts the system handles for you
- **Accepting a proposal** can jump straight to a **Project** (and marks the deal **Won**).
- **Signing a contract** marks its proposal **Accepted** and the deal **Won**, then creates the Project.

### A few links worth knowing
- A **Deal**, **Proposal** and **Contract** all attach to a **Company** (and optionally a **Contact**).
- Marking a deal **Won** accrues the owner's **commission** (pending until the client pays).
- **Money fields** (deal value, proposal/contract amounts) are part of the
  finance-facing view.`,
  },
  {
    slug: 'crm-bids',
    title: 'Bids (the proposal ledger)',
    module: 'crm',
    summary: 'One row per proposal — synced from Upwork or added by hand — in front of Leads.',
    icon: Gavel,
    roles: CRM_ROLES,
    body: `A **Bid** is one proposal we sent on a channel. The ledger sits **in front of
Leads**: the funnel reads *Bids → Leads → Deals*. It is not a lead — most bids never
become one — it is the record that lets you compare rates, bid filters and Connects
spend per proposal, long after the channel's own 90-day view has forgotten them.

### Bid ≠ Lead
- **Bid** — we applied. Mandatory for Upwork; optional for other proposal-shaped
  channels (Fiverr buyer requests, RFPs / tenders).
- **Lead** — the client engaged. Apollo and LinkedIn skip Bids and land in Leads.

### Synced vs manual rows
- **Synced** (Upwork) — a scheduled sync reads each connected Upwork account daily
  and writes one row per proposal: job, our bid, the job's bid range, the client
  record, viewed / replied / hired signals and the proposal insights.
- **Manual** — add a bid yourself for other channels, bids placed outside the
  tooling, corrections, or during a sync outage.
- **Manual wins.** Any field you edit by hand is remembered and the sync never
  overwrites it — the detail page shows how many fields are hand-edited.

### Ownership = the bidder
Each bid has an **owner** (who submitted) and a **bidder profile** (which channel
profile — founder individual, agency, …). The sync sets both from the account the
proposal came from, so the founder's bids stay the founder's and a colleague's stay
theirs. On convert the owner is copied onto the Lead and then the Deal — **the
commission on owned deals keys off it**, so it is never taken from who clicked
*Convert*.

### Two snapshots
The job's numbers keep moving after we bid, so each bid keeps:
- **At submit** — proposals, invites, interviewing, offers, hires and the client's
  last view when we entered. Frozen. Use it for *how strong was the field*.
- **Latest** — refreshed every sync. Use it for *what happened*.

**Our bid vs the job's median** (below / at / above) is derived from the at-submit
bid range and drives the roll-up split.

### Statuses and auto-close
*Submitted → Viewed → Replied → Interviewing → Hired*, plus *Declined*, *Withdrawn*
and *Closed*. The sync moves a bid forward from the channel's signals; a contract id
means **Hired**. When the latest snapshot shows the job **hired someone else** and we
hold no contract, the bid **auto-closes** (lost to another bidder) with the sync
time as *Closed*. Settled statuses never reopen.

### Convert to lead
**Convert to lead** creates the Lead **once** — source = the bid's channel, owner =
the bid's owner, job title and link carried over — and links both ways; the button
then becomes *Open lead*, and the lead shows "from bid". The sync converts
automatically the moment it sees an **interview room** for that job.

### Weekly roll-up
*Bids → Weekly roll-up* aggregates the **whole filtered set** (never just a page)
per ISO week, split by any of channel, owner, hourly **rate band**, **bid vs
median** and **boosted**: proposals, viewed, replied, interviewing, hired, net
Connects, and **cost per reply / per hire** (net Connects ÷ outcome). This is the
row the Upwork rate-experiment tracker needs. *Replied* counts replied,
interviewing and hired together. The channel's own weekly stats are a cross-check,
not the source — per-proposal *viewed* comes from the API.

### Filters
The toolbar keeps the everyday ones in reach: **search**, **status**, **Owner**
(*Anyone*, *Me* or a specific bidder) and **Submitted** (*Last 7 / 30 / 90 days*, a
month or quarter, or your own dates — the rolling ones keep rolling day to day).
Everything else sits behind **Filters**: channel, source (synced / manual), price vs
the job median, rate band, boosted, converted to a lead, client country and a minimum
client hire rate. The button shows how many are on; each one appears as a chip under
the toolbar — click its **×** to drop it, or **Reset all filters** to start over. Your
filters, *Owner: Me* included, are remembered next time. **Export** downloads exactly
what you see as CSV.`,
  },
  {
    slug: 'crm-leads',
    title: 'Leads',
    module: 'crm',
    summary: 'Capture and qualify raw interest, then convert it to a deal.',
    icon: UserPlus,
    roles: CRM_ROLES,
    body: `A **Lead** is the first sign of interest — before you know it's a real
opportunity. It's the top of the funnel.

### Statuses
- **New** — just captured, not yet looked at.
- **Qualified** — worth pursuing.
- **Disqualified** — not a fit (kept for the record).
- **Converted** — turned into a deal (happens automatically when you convert).

### How to use it
1. Add a lead (or it arrives from a channel — Website, LinkedIn, Referral, Outbound…).
   **Upwork leads arrive via Bids**: a proposal becomes a lead when the client engages
   (see *Bids*), and the lead shows "from bid" with a link back.
2. Work it and set the **status** as you learn more.
3. **Qualify it** — note the client's **pain** (why they're talking to us), whether it
   **fits** your ICP (Fits / Partial / No fit) and which **ICP segment** it matches. The
   company's industry, region and technologies show alongside, read-only.
4. When it's real, **Convert to deal** — the lead is marked *Converted* and a new
   **Deal** opens at the *Qualified* stage, carrying the owner across.

### ICP segments
Your ideal customer profiles — e.g. "EU SaaS scale-ups" or "US Shopify stores" — live under
**CRM › ICP Segments**. Sales Managers add, rename and archive them; everyone in sales picks
from them on a lead. Archiving a segment keeps it on the leads that already have it. Filter
the Leads list by fit, segment, industry, region, country or technology from **Filters**.

### Why it's here
Leads keep unproven interest out of your pipeline numbers, so your deal stats
reflect genuine opportunities — and nothing promising slips away.`,
  },
  {
    slug: 'crm-what-to-track',
    title: 'What makes a good lead',
    module: 'crm',
    summary: 'Track replies with real potential — not every application or connect.',
    icon: Target,
    roles: CRM_ROLES,
    body: `A lead is a **two-way signal of real potential** — someone engaged *and* there's
a project you could win. It is **not** every outbound action you take. Keeping this
bar high means a short, honest pipeline of winnable work instead of hundreds of
dead rows.

> **Rule of thumb:** *applied / connected = activity · replied + has potential =
> Lead · scoped / quoting = Deal.*

### Upwork
- ❌ **Don't** add a lead for every job you apply to — every proposal is a **Bid**
  (synced daily into the Bids ledger), not a lead.
- ✅ **Do** convert the bid to a lead when the **client engages**: replies to your
  proposal, messages you, invites/interviews you, or shows intent ("can you
  start…?", asks for a call or scope). The sync does this for you when an
  interview room opens; **Convert to lead** on the bid does it by hand.

### LinkedIn
- ❌ **Don't** add a lead for every connection request or cold message you send.
- ✅ **Do** add a lead when they **reply with interest** — book a call, mention a
  need, a budget, or a timeline.

### The threshold (any channel)
There's a **real person engaging** *and* a **plausible project**. If you can't
picture qualifying or pitching it yet, it's not a lead — it's still activity.

### Once it's a lead
Set the **source** (Upwork / LinkedIn / Website / Referral / Outbound / Fiverr / RFP —
a lead converted from a bid gets it from the bid's channel) and move the
**status**: **New** (they responded) → **Qualified** (worth pursuing) →
**Disqualified** (not a fit) → **convert to a Deal** when it's a concrete opportunity.

### Where the rest goes
Raw *applications sent* live in the **Bids** ledger (one row per proposal, with the
job's competition and the outcome), where the weekly roll-up reads them. LinkedIn
*connections sent* stay in LinkedIn's own dashboard. Your Leads list stays a tidy
list of genuinely winnable opportunities.`,
  },
  {
    slug: 'crm-tags',
    title: 'Tagging leads',
    module: 'crm',
    summary: 'Use tags (Inbound, Outbound, Upwork, Hot…) to slice your pipeline.',
    icon: Tags,
    roles: CRM_ROLES,
    body: `**Tags** are free-form labels you stick on a lead to group and find them fast —
on top of the built-in *source* and *status*.

### How to use it
- Open a lead and use the **Tags** panel to add or remove labels (type to create a
  new one or pick an existing tag; each has a colour).
- On the **Leads list**, filter by tag to focus (e.g. show only **Hot**).
- Tags are shared org-wide, so the whole team uses the same vocabulary — keep them
  consistent.

### Suggested tags
- **Channel:** \`Inbound\`, \`Outbound\`, \`Upwork\`, \`LinkedIn\`, \`Referral\`
- **Temperature:** \`Hot\`, \`Warm\`, \`Cold\`
- **Context (optional):** \`Retainer\`, \`Quick-win\`, \`Enterprise\`, \`Follow-up\`

> Tip: keep the list short and shared. A handful of meaningful tags beats dozens of
> one-off labels nobody else uses.

### Why it's here
Status tells you *where* a lead is; tags tell you *what kind* it is. Together they
let you answer "show me my **Hot Inbound** leads" in one click — so you spend time
on the ones most likely to win.`,
  },
  {
    slug: 'crm-contacts',
    title: 'Contacts',
    module: 'crm',
    summary: 'The people you deal with — linked to their company.',
    icon: Contact,
    roles: CRM_ROLES,
    body: `A **Contact** is a person — the human you actually talk to. Each contact
usually belongs to a **Company**.

### How to use it
- Add the people you work with and link them to their **company**.
- Reference a contact on a **deal**, **proposal** or **contract** so it's clear
  who the counterpart is.
- Open a contact to see their details and related activity.

### Why it's here
Deals are won with people, not logos. Keeping contacts distinct from companies
means you always know *who* to call — and the history of every conversation with
them.`,
  },
  {
    slug: 'crm-companies',
    title: 'Companies',
    module: 'crm',
    summary: 'The client accounts your deals and invoices belong to.',
    icon: Building2,
    roles: CRM_ROLES,
    body: `A **Company** is a client account — the organization you're selling to and,
later, billing.

### How to use it
- Create a company, then attach its **contacts**, **deals**, **proposals** and **contracts**.
- Open a company to see everything tied to it in one place.
- Fill in its **Profile** — **industry** and **technologies** (their stack; pick an existing
  tag so spellings stay consistent). **Region** follows from the country. The profile shows on
  every lead for this company, and the Companies list filters by it.

### Why it's here
The company is the anchor the whole relationship hangs off — from first deal to
the invoices Finance sends. One tidy account record keeps sales and billing in sync.`,
  },
  {
    slug: 'crm-deals',
    title: 'Deals & Pipeline',
    module: 'crm',
    summary: 'Track opportunities through stages on the board, with value and owner.',
    icon: TrendingUp,
    roles: CRM_ROLES,
    body: `A **Deal** is a real opportunity you're working to close. The **Pipeline**
board is the same deals laid out by stage so you can see your funnel at a glance.

### The stages
**Qualified → Proposal → Negotiation → Won / Lost.** Each stage carries a default
**win-probability** (25% → 50% → 75%, then 100% Won / 0% Lost) that nudges the
deal's forecast as it moves; you can fine-tune it.

### How to use it
- **Move a deal** by dragging it across the **Pipeline** board, or change its stage on the deal.
- Set the **owner** (who's responsible) and the deal **value**.
- Set the **commission %** — the rate the owner earns (seeded from their default).
- Choose the **commission basis** — pay on the client's **first invoice (new clients
  only)** or **all paid invoices** (see Finance → *Setting up sales commission*).
- Mark **Won** when it closes — this accrues the owner's commission (pending until paid),
  provided the owner is commission-eligible.
- Mark **Lost** and say **why** — dropping a deal on *Lost* (or choosing Lost on the deal)
  asks for a reason (price, timing, competitor, no budget, no response, not a fit, scope,
  other) and an optional note. Cancel and the deal stays where it was. Reopening a lost deal
  clears the reason.
- A deal converted from a lead shows **From lead** — the lead's pain, ICP fit and segment.

### Good to know
- Changing a deal's **owner** and setting the **commission % / basis** are
  admin-controlled — you can see your rate but don't set your own pay.
- A deal converted from a lead takes the lead's **current owner**, never whoever clicked
  *Convert*. A lead that came from a **Bid** starts out owned by the person who placed the
  bid, so the deal and its commission go to the bidder — unless the lead was reassigned
  before converting (see CRM → *Bids*).
- **Money fields** (value, commission) are part of the finance-facing view.

### Why it's here
A single, honest view of what's in flight, who owns it, and how likely it is to
close — the heartbeat of the sales team.`,
  },
  {
    slug: 'crm-proposals',
    title: 'Proposals',
    module: 'crm',
    summary: 'The priced offer you send for sign-off — with snapshotted line items.',
    icon: FileText,
    roles: CRM_MONEY_ROLES,
    body: `A **Proposal** is the priced offer for a deal: the services, quantities and
rates you're putting forward.

### Statuses
**Draft → Sent → Accepted / Rejected.**

### How to use it
1. Create a proposal for a deal and add **line items** (pull from the Service
   Catalog or type your own).
2. Send it for the client's decision.
3. On **Accept**, the system can take it **straight through to a Project** (and
   marks the deal **Won**).

### Why it's here — snapshots
When you add a line, its **name and rate are snapshotted**. Editing the Service
Catalog later never changes an already-issued proposal — what the client agreed
to stays exactly as it was. That's what keeps your offers trustworthy and auditable.`,
  },
  {
    slug: 'crm-contracts',
    title: 'Contracts',
    module: 'crm',
    summary: 'The signed agreement — signing it provisions the project automatically.',
    icon: FileSignature,
    roles: CRM_MONEY_ROLES,
    body: `A **Contract** is the agreement that locks in the work — usually following
an accepted proposal.

### Statuses
**Draft → Sent for signature → Signed / Cancelled.**

### What signing does (in one step)
When a contract is **Signed**, the system automatically:
- provisions a **Project** so delivery can start,
- marks the linked **proposal** as **Accepted**,
- moves the **deal** to **Won**.

### How to use it
1. Create a contract (often from the accepted proposal).
2. Send it for signature.
3. **Sign** it — and the project, proposal and deal all update together.

### Why it's here
It's the clean handoff from *selling* to *delivering* — one action ties the paperwork,
the pipeline and the new project together, with no manual re-entry.`,
  },
  {
    slug: 'crm-activities',
    title: 'Activities',
    module: 'crm',
    summary: 'Log calls, meetings, emails and notes against any record.',
    icon: Activity,
    roles: CRM_ROLES,
    body: `**Activities** are the running history of your interactions — calls,
meetings, emails, notes and tasks — logged against a lead, contact, company or deal.

### How to use it
- Record what happened (type, who, when, a short note).
- Read a record's timeline to see the full story before your next touch.

### Why it's here
Deals span weeks and hands. A shared timeline means anyone can pick up the
relationship with full context — nothing lives only in someone's inbox or memory.`,
  },
];
